import * as path from 'node:path';
import type { SessionParent } from '@buildautomaton/types';
import { discoverSessionWorktreesUnderSessionWorktreeRoot } from '../discovery/index.js';
import type { SessionWorktreeBinding } from '../session-worktree-binding.js';
import { parseSessionParent } from './parse-session-parent.js';
import type { SessionWorktreeCache } from './session-worktree-cache.js';
import { yieldToEventLoop } from '../../runtime/yield-to-event-loop.js';

export async function resolveExplicitSessionParentPath(params: {
  sessionId: string;
  sessionParent: SessionParent | null | undefined;
  parentPathRaw: string;
  cache: SessionWorktreeCache;
  discover: () => Promise<SessionWorktreeBinding | null>;
}): Promise<string> {
  const resolved = path.resolve(params.parentPathRaw);
  if (parseSessionParent(params.sessionParent) !== 'worktrees_root') {
    return resolved;
  }

  const rememberAndReturn = (binding: SessionWorktreeBinding): string => {
    params.cache.remember(params.sessionId, binding);
    return params.cache.getSessionParentPath(params.sessionId) ?? resolved;
  };

  const diskFirst = await params.discover();
  if (diskFirst) return rememberAndReturn(diskFirst);

  const fromRoot = await discoverSessionWorktreesUnderSessionWorktreeRoot(resolved, params.sessionId);
  if (fromRoot) return rememberAndReturn(fromRoot);

  let cur = resolved;
  for (let i = 0; i < 16; i++) {
    await yieldToEventLoop();
    const tryRoot = await discoverSessionWorktreesUnderSessionWorktreeRoot(cur, params.sessionId);
    if (tryRoot) return rememberAndReturn(tryRoot);
    const next = path.dirname(cur);
    if (next === cur) break;
    cur = next;
  }

  return resolved;
}
